// Bounded line comparison for local recovery records. Large replacements use
// one exact replacement block instead of quadratic work on the UI thread.
export function projectDiffPalette(dark) {
  return dark
    ? { added: '#7ee2a8', removed: '#ffaaa5', addedBackground: '#123825', removedBackground: '#422020', hunk: '#9fc7ff', hunkBackground: '#17283e' }
    : { added: '#176b37', removed: '#ad2828', addedBackground: '#dff5e6', removedBackground: '#fde8e7', hunk: '#285e9d', hunkBackground: '#eaf2fc' };
}

// Sources and the full review use the same status colors. Check both Git
// columns: a staged addition can also have an unstaged deletion or conflict.
export function projectFileColor(item, palette) {
  const gitStates = [item.index, item.workingTree];
  if (item.status === 'conflict' || item.status === 'failed' || item.operation === 'trash'
    || gitStates.some(state => state === 'D' || state === 'U')
    || ['AA', 'DD'].includes(`${item.index}${item.workingTree}`)) return palette.removed;
  if (gitStates.some(state => ['?', 'A', 'C'].includes(state))
    || ['copy', 'save', 'mkdir'].includes(item.operation)) return palette.added;
  return palette.hunk;
}

// Only hunk content affects line numbers/counts. In particular +++/--- file
// headers are metadata, while a content line beginning +++ inside a hunk is added.
export function parseProjectGitDiff(diff = '', limit = 700) {
  const rows = [];
  let oldLine = 0, newLine = 0, oldRemaining = 0, newRemaining = 0, added = 0, removed = 0;
  const lines = diff.split('\n');
  if (lines[lines.length - 1] === '') lines.pop();
  for (const line of lines) {
    const hunk = /^@@ -(\d+)(?:,(\d+))? \+(\d+)(?:,(\d+))? @@/.exec(line);
    let row;
    if (hunk) {
      oldLine = Number(hunk[1]); newLine = Number(hunk[3]);
      oldRemaining = Number(hunk[2] ?? 1); newRemaining = Number(hunk[4] ?? 1);
      row = { kind: 'hunk', text: line };
    } else if (line.startsWith('+') && newRemaining > 0) {
      row = { kind: 'added', text: line.slice(1), oldLine: null, newLine: newLine++ };
      newRemaining--; added++;
    } else if (line.startsWith('-') && oldRemaining > 0) {
      row = { kind: 'removed', text: line.slice(1), oldLine: oldLine++, newLine: null };
      oldRemaining--; removed++;
    } else if (line.startsWith(' ') && oldRemaining > 0 && newRemaining > 0) {
      row = { kind: 'context', text: line.slice(1), oldLine: oldLine++, newLine: newLine++ };
      oldRemaining--; newRemaining--;
    } else if (!/^\+\+\+ |^--- |^diff |^index |^\\ |^@@/.test(line) && (line.startsWith('+') || line.startsWith('-'))) {
      // Model replies sometimes omit unified headers. Still color the explicit
      // +/- content without inventing source line numbers.
      const isAdded = line[0] === '+';
      row = { kind: isAdded ? 'added' : 'removed', text: line.slice(1) };
      if (isAdded) added++; else removed++;
    } else {
      row = { kind: 'meta', text: line };
    }
    if (rows.length < limit) rows.push(row);
  }
  return { rows, added, removed, truncated: lines.length > limit };
}

// An untracked file has no Git baseline. Show its current lines as additions,
// with the same bounded rendering as other diffs. A final newline terminates
// the last line; it is not an extra empty added line.
export function projectNewFileDiff(content = '', limit = 600) {
  const lines = content.split('\n');
  if (!content || content.endsWith('\n')) lines.pop();
  return { rows: lines.slice(0, limit).map((text, index) => ({ kind: 'added', text, oldLine: null, newLine: index + 1 })),
    added: lines.length, removed: 0, truncated: lines.length > limit };
}

export function projectChangeDiff(before = '', after = '') {
  if (before === after) return { rows: [], added: 0, removed: 0, truncated: false };
  // A final newline terminates the last line; it is not an extra added line.
  // Retain each terminator in the comparison so adding/removing a final newline
  // still appears as a changed line, just as it does in a Git diff.
  const splitLines = text => {
    const lines = text ? text.split('\n', 5002) : [];
    if (text.endsWith('\n') && lines.length <= 5001) lines.pop();
    return lines.map((line, index) => index < lines.length - 1 || text.endsWith('\n') ? `${line}\n` : line);
  };
  const a = splitLines(before), b = splitLines(after);
  if (a.length > 5000 || b.length > 5000) return { rows: [], added: null, removed: null, truncated: true };
  let start = 0, endA = a.length, endB = b.length;
  while (start < endA && start < endB && a[start] === b[start]) start++;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA--; endB--; }
  const rows = [], changed = [];
  let approximate = false;
  const displayLine = text => text.endsWith('\n') ? text.slice(0, -1) : text;
  const push = (kind, text, oldLine, newLine) => changed.push({ kind, text: displayLine(text), oldLine, newLine });
  const n = endA - start, m = endB - start;
  if (n * m <= 250000 && n + m < 4000) {
    const table = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
    for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--)
      table[i][j] = a[start + i] === b[start + j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    let i = 0, j = 0;
    while (i < n || j < m) {
      if (i < n && j < m && a[start + i] === b[start + j]) { push('context', a[start + i], start + i + 1, start + j + 1); i++; j++; }
      else if (i < n && (j === m || table[i + 1][j] >= table[i][j + 1])) { push('removed', a[start + i], start + i + 1, null); i++; }
      else { push('added', b[start + j], null, start + j + 1); j++; }
    }
  } else {
    const oldLines = new Set(a.slice(start, endA));
    approximate = b.slice(start, endB).some(line => oldLines.has(line));
    for (let i = start; i < endA; i++) push('removed', a[i], i + 1, null);
    for (let j = start; j < endB; j++) push('added', b[j], null, j + 1);
  }
  for (let i = Math.max(0, start - 3); i < start; i++) rows.push({ kind: 'context', text: displayLine(a[i]), oldLine: i + 1, newLine: i + 1 });
  rows.push(...changed.slice(0, 600));
  for (let i = 0; i < Math.min(3, a.length - endA); i++) rows.push({ kind: 'context', text: displayLine(a[endA + i]), oldLine: endA + i + 1, newLine: endB + i + 1 });
  return { rows, added: changed.filter(r => r.kind === 'added').length, removed: changed.filter(r => r.kind === 'removed').length, truncated: changed.length > 600, approximate };
}
